import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid";
import { RootState, AppDispatch } from "../redux/store";
import { setTableState, removeTableState } from "../redux/datagrid/tableSlice";

const TABLE_PREFERENCES_VERSION = "v1";

const createDefaultState = (allColumns: string[]) => ({
  filters: { items: [], quickFilterValues: [] } as GridFilterModel,
  columns: allColumns,
  sort: [] as GridSortModel,
  global_search: "",
  expiry: 0,
});

export const useTableState = (tableName: string, allColumns: string[]) => {
  const dispatch = useDispatch<AppDispatch>();
  const tableState = useSelector((state: RootState) => state.table[tableName]);
  const userId = useSelector(
    (state: RootState) =>
      state.user.user?.id_user ?? state.auth.user?.id_user ?? "anonymous"
  );
  const storageKey = useMemo(
    () => `flex:table-preferences:${TABLE_PREFERENCES_VERSION}:${userId}:${tableName}`,
    [tableName, userId]
  );

  useEffect(() => {
    const defaultState = createDefaultState(allColumns);

    try {
      const savedState =
        localStorage.getItem(storageKey) ?? localStorage.getItem(tableName);

      if (!savedState) {
        dispatch(setTableState({ tableName, storageKey, state: defaultState }));
        return;
      }

      const parsedState = JSON.parse(savedState);
      const savedColumns = Array.isArray(parsedState?.columns)
        ? parsedState.columns.filter(
            (field: unknown): field is string =>
              typeof field === "string" && allColumns.includes(field)
          )
        : allColumns;

      dispatch(
        setTableState({
          tableName,
          storageKey,
          state: {
            ...defaultState,
            ...parsedState,
            columns: savedColumns,
            expiry: 0,
          },
        })
      );

      if (localStorage.getItem(storageKey) && localStorage.getItem(tableName)) {
        localStorage.removeItem(tableName);
      }
    } catch {
      localStorage.removeItem(storageKey);
      dispatch(setTableState({ tableName, storageKey, state: defaultState }));
    }
    // Callers construct allColumns inline, so it must not retrigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, storageKey, tableName]);

  const updateTableState = (newState: Partial<typeof tableState>) => {
    dispatch(
      setTableState({
        tableName,
        storageKey,
        state: {
          ...createDefaultState(allColumns),
          ...tableState,
          ...newState,
          expiry: 0,
        },
      })
    );
  };

  const updateColumns = (columns: string[]) => updateTableState({ columns });
  const updateSort = (sort: GridSortModel) => updateTableState({ sort });

  const initializeState = () => {
    dispatch(
      setTableState({
        tableName,
        storageKey,
        state: createDefaultState(allColumns),
      })
    );
  };

  const clearTableState = () => {
    dispatch(removeTableState({ tableName, storageKey }));
    initializeState();
  };

  return {
    tableState,
    updateTableState,
    clearTableState,
    updateColumns,
    updateSort,
  };
};
